import AxiosService from '@/Services/AxiosService';
import LoaderService from '@/Services/LoaderService';
import { Inject, Service } from 'vue-di-container';
import {
    TemplateFilterCheckOutcome,
    TemplateFilterCheckResultModel,
    TemplateFilterCheckSenderListModel,
    TemplateFilterCheckSenderPageModel,
} from './TemplateFilterCheckModel';

/**
 * Transport for the template-validation page. Calls go through Ocelot with the user's own Bearer.
 *
 * `validateStatus` opts these calls out of the shared response interceptor: it handles only 500 and 409,
 * never rethrows, and dereferences `error.response` unguarded, so a 403 would vanish and a refused
 * connection would throw inside the interceptor.
 */
@Service()
export default class TemplateFilterCheckService {
    @Inject(AxiosService) public axiosService!: AxiosService;
    @Inject(LoaderService) public loaderService!: LoaderService;

    /** The caller's selectable sender names, and the message length the API will accept. */
    public async getSenders(): Promise<TemplateFilterCheckOutcome<TemplateFilterCheckSenderListModel>> {
        this.loaderService.ShowLoader();
        try {
            const url = window.$config.HostName + 'api/message-template/validation-senders';
            const response = await this.axiosService.axiosInstance.get(url, {
                validateStatus: () => true,
            });
            if (!response) {
                return this.failure('unavailable');
            }
            if (response.status === 200 && response.data && response.data.isSuccess === true) {
                const result = response.data.result || {};
                const max = result.maxMessageLength;
                // Treated as a failed load rather than defaulted: a page that invented its own limit could
                // cap the box at a number the API does not enforce, and would print it in the error text.
                if (typeof max !== 'number' || !isFinite(max) || max <= 0) {
                    return this.failure('unavailable');
                }
                return {
                    ok: true,
                    data: { senders: result.senders || [], maxMessageLength: max },
                    errorCode: null,
                };
            }
            return this.failure(this.codeFor(response));
        } catch (exception) {
            return this.failure('unavailable');
        } finally {
            this.loaderService.HideLoader();
        }
    }

    /**
     * One page of sender names, filtered by prefix.
     *
     * No global loader: this runs on every debounced keystroke and on every scroll to the bottom, and a
     * full-screen spinner for each of those would make the picker unusable. The page shows its own
     * inline indicator instead.
     */
    public async searchSenders(
        search: string,
        pageIndex: number,
        pageSize: number): Promise<TemplateFilterCheckOutcome<TemplateFilterCheckSenderPageModel>> {
        try {
            const url = window.$config.HostName + 'api/message-template/validation-senders/search';
            const response = await this.axiosService.axiosInstance.get(url, {
                params: { search, pageIndex, pageSize },
                validateStatus: () => true,
            });
            if (!response) {
                return this.failure('unavailable');
            }
            if (response.status === 200 && response.data && response.data.isSuccess === true) {
                const page = this.asPage(response.data.result);
                // A success whose body does not carry the fields the picker needs is treated as a failed
                // call, not as an empty page: an empty page would read as "no such sender".
                return page === null
                    ? this.failure('unavailable')
                    : { ok: true, data: page, errorCode: null };
            }
            return this.failure(this.codeFor(response));
        } catch (exception) {
            return this.failure('unavailable');
        }
    }

    /** Validates a page body, returning null when any field the picker depends on is missing. */
    private asPage(result: any): TemplateFilterCheckSenderPageModel | null {
        if (!result || typeof result !== 'object') {
            return null;
        }
        const max = result.maxMessageLength;
        if (typeof max !== 'number' || !isFinite(max) || max <= 0) {
            return null;
        }
        if (typeof result.hasMore !== 'boolean'
            || typeof result.pageIndex !== 'number'
            || typeof result.pageSize !== 'number'
            || !(result.senders instanceof Array)) {
            return null;
        }
        return {
            senders: result.senders,
            maxMessageLength: max,
            pageIndex: result.pageIndex,
            pageSize: result.pageSize,
            hasMore: result.hasMore,
        };
    }

    /** Submits one message for a verdict. A 200 carries the answer, match or not. */
    public async validate(
        senderName: string,
        message: string): Promise<TemplateFilterCheckOutcome<TemplateFilterCheckResultModel>> {
        this.loaderService.ShowLoader();
        try {
            const url = window.$config.HostName + 'api/message-template/validate-message';
            const response = await this.axiosService.axiosInstance.post(
                url,
                { senderName, message },
                { validateStatus: () => true });

            if (!response) {
                return this.failure('unavailable');
            }
            if (response.status === 200 && response.data && response.data.isSuccess === true) {
                return { ok: true, data: response.data.result as TemplateFilterCheckResultModel, errorCode: null };
            }
            return this.failure(this.codeFor(response));
        } catch (exception) {
            return this.failure('unavailable');
        } finally {
            this.loaderService.HideLoader();
        }
    }

    /** Maps a failure to a translation key, falling back on status for gateway responses with no envelope. */
    private codeFor(response: any): string {
        const fromServer = response && response.data && response.data.errorMessage;
        if (fromServer && this.isKnownCode(fromServer)) {
            return fromServer;
        }
        switch (response.status) {
            case 400: return 'inputRequired';
            case 401: return 'unauthorized';
            case 403: return 'forbidden';
            case 404: return 'disabled';
            case 504: return 'timeout';
            default: return 'unavailable';
        }
    }

    /** Mirrors TemplateMatchErrorCodes on the server; guards against rendering an unknown string as a key. */
    private isKnownCode(code: string): boolean {
        return [
            'inputRequired',
            'messageTooLong',
            'senderNotOwned',
            'sendersUnavailable',
            'notConfigured',
            'unavailable',
            'timeout',
            'unauthorized',
            'forbidden',
            'disabled',
        ].indexOf(code) >= 0;
    }

    /** Builds a failed outcome carrying only a translation key. */
    private failure<T>(errorCode: string): TemplateFilterCheckOutcome<T> {
        return { ok: false, data: null, errorCode };
    }
}
